import { ApparatDeviceBox, AudioFileBox, CubedDeviceBox, MIDIOutputDeviceBox, MIDIOutputParameterBox, NanoDeviceBox, NeonDeviceBox, PlayfieldDeviceBox, PlayfieldSampleBox, SoundfontDeviceBox, SoundfontFileBox, TapeDeviceBox, VaporisateurDeviceBox } from "@opendaw/studio-boxes";
import { CubedStep as CubedStepCodec } from "@opendaw/studio-adapters";
import { asInstanceOf, clamp, isDefined, isNull, panic, UUID } from "@opendaw/lib-std";
import { Accessors, Facade, Props } from "../Common";
import { Fields } from "../Fields";
import { Guard } from "../Guard";
import { AudioFiles } from "../AudioFiles";
import { EffectChain } from "./EffectChain";
import { DeviceBoxes } from "./DeviceBoxes";
import { MIDIEffectImpls } from "./MIDIEffects";
import { AudioEffectImpls } from "./AudioEffects";
import { ScriptSupport } from "./ScriptDevices";
import { Facades } from "../Facades";
import { TrackImpls } from "../timeline/Tracks";
import { TrackType } from "@opendaw/studio-adapters";
export class InstrumentFacade extends Facade {
    constructor(context, box) {
        super(context, box);
        this.bind({ label: box.label, icon: box.icon, enabled: box.enabled, minimized: box.minimized });
    }
    get audioUnit() { return Facades.audioUnitOf(this.context, this.box); }
    remove() { this.audioUnit.remove(); }
}
export class VaporisateurImpl extends InstrumentFacade {
    key = "Vaporisateur";
    constructor(context, box) {
        super(context, box);
        this.bind({
            cutoff: box.cutoff, resonance: box.resonance, filterOrder: box.filterOrder,
            filterEnvelope: box.filterEnvelope, filterKeyboard: box.filterKeyboard,
            attack: box.attack, decay: box.decay, sustain: box.sustain, release: box.release,
            voicingMode: box.voicingMode, glideTime: box.glideTime, unisonCount: box.unisonCount,
            unisonDetune: box.unisonDetune, unisonStereo: box.unisonStereo,
            lfo: {
                waveform: box.lfo.waveform, rate: box.lfo.rate, sync: box.lfo.sync,
                targetTune: box.lfo.targetTune, targetCutoff: box.lfo.targetCutoff, targetVolume: box.lfo.targetVolume
            },
            oscillators: box.oscillators.fields().map(osc => ({ waveform: osc.waveform, volume: osc.volume, octave: osc.octave, tune: osc.tune })),
            noise: { attack: box.noise.attack, hold: box.noise.hold, release: box.noise.release, volume: box.noise.volume }
        });
    }
}
export class PlayfieldSlotImpl extends Facade {
    static wrap(context, box) {
        return context.facade(box, () => new PlayfieldSlotImpl(context, box));
    }
    #midiChain;
    #audioChain;
    constructor(context, box) {
        super(context, box);
        this.bind({
            note: box.index, icon: box.icon, enabled: box.enabled, minimized: box.minimized, mute: box.mute,
            solo: box.solo, exclude: box.exclude, polyphone: box.polyphone, gate: box.gate, pitch: box.pitch,
            sampleStart: box.sampleStart, sampleEnd: box.sampleEnd, attack: box.attack, release: box.release,
            volume: box.volume, panning: box.panning
        });
        this.#midiChain = new EffectChain(context, box.midiEffects, box => MIDIEffectImpls.wrap(context, box));
        this.#audioChain = new EffectChain(context, box.audioEffects, box => AudioEffectImpls.wrap(context, box));
    }
    get playfield() {
        const deviceBox = this.box.device.targetVertex.unwrap("slot has no playfield").box;
        return InstrumentImpls.wrap(this.context, deviceBox);
    }
    get sample() {
        return AudioFiles.toSample(this.context, asInstanceOf(this.box.file.targetVertex.unwrap("slot has no file").box, AudioFileBox));
    }
    get midiEffects() { return this.#midiChain.list(); }
    get audioEffects() { return this.#audioChain.list(); }
    addMIDIEffect(key, props, index) {
        Guard.oneOf(key, Object.keys(DeviceBoxes.MIDIEffectLabels), "key");
        return this.#midiChain.add(at => DeviceBoxes.createMIDIEffect(this.context.boxGraph, key, this.box.midiEffects, at), props, index);
    }
    addAudioEffect(key, props, index) {
        Guard.oneOf(key, Object.keys(DeviceBoxes.AudioEffectLabels), "key");
        return this.#audioChain.add(at => DeviceBoxes.createAudioEffect(this.context.boxGraph, key, this.box.audioEffects, at), props, index);
    }
}
export class PlayfieldImpl extends InstrumentFacade {
    key = "Playfield";
    constructor(context, box) { super(context, box); }
    get slots() {
        return this.box.samples.pointerHub.incoming()
            .map(({ box }) => PlayfieldSlotImpl.wrap(this.context, asInstanceOf(box, PlayfieldSampleBox)))
            .sort((a, b) => a.note - b.note);
    }
    slot(note) {
        const index = Guard.integer(note, "note");
        return this.slots.find(slot => slot.note === index) ?? null;
    }
    addSample(sample, props) {
        return this.context.edit(() => {
            const fileBox = AudioFiles.use(this.context, sample);
            const note = isDefined(props?.note) ? Guard.int32({ min: 0, max: 127 }, props.note, "note") : this.#nextFreeNote();
            this.slot(note)?.remove();
            const slotBox = PlayfieldSampleBox.create(this.context.boxGraph, UUID.generate(), box => {
                box.device.refer(this.box.samples);
                box.file.refer(fileBox);
                box.index.setValue(note);
            });
            const rest = Props.without(props, "note");
            return Props.apply(PlayfieldSlotImpl.wrap(this.context, slotBox), rest);
        });
    }
    #nextFreeNote() {
        const taken = new Set(this.slots.map(slot => slot.note));
        for (let note = 60; note < 128; note++) {
            if (!taken.has(note)) {
                return note;
            }
        }
        for (let note = 59; note >= 0; note--) {
            if (!taken.has(note)) {
                return note;
            }
        }
        return panic("All 128 notes are taken");
    }
}
export class NanoImpl extends InstrumentFacade {
    key = "Nano";
    constructor(context, box) {
        super(context, box);
        this.bind({ volume: box.volume, release: box.release });
    }
    get sample() {
        const fileBox = Accessors.pointerBox(this.box.file, AudioFileBox);
        return isNull(fileBox) ? null : AudioFiles.toSample(this.context, fileBox);
    }
    set sample(sample) {
        AudioFiles.assign(this.context, this.box.file, () => isNull(sample) ? null : AudioFiles.use(this.context, sample));
    }
}
export class SoundfontImpl extends InstrumentFacade {
    key = "Soundfont";
    constructor(context, box) {
        super(context, box);
        this.bind({ presetIndex: box.presetIndex });
    }
    get file() {
        const fileBox = Accessors.pointerBox(this.box.file, SoundfontFileBox);
        return isNull(fileBox) ? null : AudioFiles.toSoundfont(fileBox);
    }
    set file(file) {
        AudioFiles.assign(this.context, this.box.file, () => isNull(file) ? null : AudioFiles.useSoundfont(this.context.boxGraph, file));
    }
}
export class MIDIOutputParameterImpl extends Facade {
    static wrap(context, box) {
        return context.facade(box, () => new MIDIOutputParameterImpl(context, box));
    }
    constructor(context, box) {
        super(context, box);
        this.bind({ label: box.label, controller: box.controller, value: box.value });
    }
    remove() {
        this.context.edit(() => {
            const tracks = Facades.audioUnitBoxOf(this.box).tracks;
            this.box.delete();
            TrackImpls.reindex(tracks);
        });
    }
}
export class MIDIOutputImpl extends InstrumentFacade {
    key = "MIDIOutput";
    constructor(context, box) {
        super(context, box);
        this.bind({ channel: box.channel });
    }
    get parameters() {
        return this.box.parameters.pointerHub.incoming()
            .map(({ box }) => MIDIOutputParameterImpl.wrap(this.context, asInstanceOf(box, MIDIOutputParameterBox)));
    }
    addParameter(props) {
        return this.context.edit(() => {
            const box = MIDIOutputParameterBox.create(this.context.boxGraph, UUID.generate(), box => {
                box.owner.refer(this.box.parameters);
                box.label.setValue("CC");
            });
            const audioUnitBox = Facades.audioUnitBoxOf(this.box);
            TrackImpls.create(this.context, audioUnitBox.tracks, TrackType.Value, box.value, null);
            return Props.apply(MIDIOutputParameterImpl.wrap(this.context, box), props);
        });
    }
}
export class TapeImpl extends InstrumentFacade {
    key = "Tape";
    constructor(context, box) {
        super(context, box);
        this.bind({ flutter: box.flutter, wow: box.wow, noise: box.noise, saturation: box.saturation });
    }
}
export class NeonImpl extends InstrumentFacade {
    key = "Neon";
    constructor(context, box) {
        super(context, box);
        const envelope = (field) => ({
            rate1: field.rate1, rate2: field.rate2, rate3: field.rate3, rate4: field.rate4,
            rate5: field.rate5, rate6: field.rate6, rate7: field.rate7, rate8: field.rate8,
            level1: field.level1, level2: field.level2, level3: field.level3, level4: field.level4,
            level5: field.level5, level6: field.level6, level7: field.level7, level8: field.level8,
            sustain: field.sustain, end: field.end
        });
        this.bind({
            lineSelect: box.lineSelect, modulation: box.modulation, octave: box.octave, detune: box.detune,
            glideTime: box.glideTime, tune: box.tune, voicingMode: box.voicingMode,
            vibrato: { wave: box.vibrato.wave, delay: box.vibrato.delay, rate: box.vibrato.rate, depth: box.vibrato.depth },
            lines: box.lines.fields().map(line => ({ wave1: line.wave1, wave2: line.wave2, dcwKeyFollow: line.dcwKeyFollow, dcaKeyFollow: line.dcaKeyFollow })),
            envelopes: box.envelopes.fields().map(envelope)
        });
    }
}
const createCubedStep = (context, field, name) => {
    const read = () => CubedStepCodec.unpack(field.getValue());
    const write = (step) => context.edit(() => field.setValue(CubedStepCodec.pack(step)));
    return Object.freeze({
        get note() { return read().note; },
        set note(value) { write({ ...read(), note: Guard.int32({ min: 0, max: 127 }, value, `${name}.note`) }); },
        get active() { return read().active; },
        set active(value) { write({ ...read(), active: Guard.boolean(value, `${name}.active`) }); },
        get slide() { return read().slide; },
        set slide(value) { write({ ...read(), slide: Guard.boolean(value, `${name}.slide`) }); },
        get accent() { return read().accent; },
        set accent(value) { write({ ...read(), accent: Guard.boolean(value, `${name}.accent`) }); }
    });
};
const createCubedPattern = (context, pattern, index) => {
    const steps = Object.freeze(pattern.steps.fields()
        .map((field, stepIndex) => createCubedStep(context, field, `patterns.${index}.steps.${stepIndex}`)));
    const target = {
        steps,
        setSteps: (values) => context.edit(() => {
            if (!Array.isArray(values)) {
                return panic(new TypeError("setSteps: expected an array"));
            }
            if (values.length > steps.length) {
                return panic(new RangeError(`setSteps: at most ${steps.length} steps`));
            }
            values.forEach((value, stepIndex) => Props.apply(steps[stepIndex], value, `steps.${stepIndex}`));
            pattern.length.setValue(clamp(values.length, 1, steps.length));
        })
    };
    Fields.bind(context, target, { length: pattern.length }, `patterns.${index}.`);
    return target;
};
export class CubedImpl extends InstrumentFacade {
    key = "Cubed";
    patterns;
    constructor(context, box) {
        super(context, box);
        this.bind({
            tuning: box.tuning, cutoff: box.cutoff, resonance: box.resonance, envMod: box.envMod,
            decay: box.decay, accent: box.accent, volume: box.volume, waveform: box.waveform,
            patternIndex: box.patternIndex
        });
        this.patterns = Object.freeze(box.patterns.fields().map((pattern, index) => createCubedPattern(context, pattern, index)));
    }
}
export class ApparatImpl extends InstrumentFacade {
    key = "Apparat";
    #script;
    constructor(context, box) {
        super(context, box);
        this.#script = new ScriptSupport(context, box, "apparat");
    }
    get code() { return this.#script.code; }
    set code(source) { this.#script.code = source; }
    get parameters() { return this.#script.parameters; }
    get samples() { return this.#script.samples; }
    parameter(label) { return this.#script.parameter(label); }
    sample(label) { return this.#script.sample(label); }
}
export var InstrumentImpls;
(function (InstrumentImpls) {
    InstrumentImpls.wrap = (context, box) => context.facade(box, () => {
        if (box instanceof VaporisateurDeviceBox) {
            return new VaporisateurImpl(context, box);
        }
        if (box instanceof PlayfieldDeviceBox) {
            return new PlayfieldImpl(context, box);
        }
        if (box instanceof NanoDeviceBox) {
            return new NanoImpl(context, box);
        }
        if (box instanceof SoundfontDeviceBox) {
            return new SoundfontImpl(context, box);
        }
        if (box instanceof MIDIOutputDeviceBox) {
            return new MIDIOutputImpl(context, box);
        }
        if (box instanceof TapeDeviceBox) {
            return new TapeImpl(context, box);
        }
        if (box instanceof NeonDeviceBox) {
            return new NeonImpl(context, box);
        }
        if (box instanceof CubedDeviceBox) {
            return new CubedImpl(context, box);
        }
        if (box instanceof ApparatDeviceBox) {
            return new ApparatImpl(context, box);
        }
        return panic(`${box.name} is not a supported instrument`);
    });
    InstrumentImpls.isBox = (box) => DeviceBoxes.isInstrumentBox(box.name);
})(InstrumentImpls || (InstrumentImpls = {}));
